import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spin, Alert, Avatar, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
} from '@syncfusion/ej2-react-grids';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import type { RootState } from '@/store';
import {
  getDashboardMetric12,
  type DashboardMetric12ProcessOwnerListing,
  type DashboardMetric12AssignedProcess,
} from '@/services/dashboardService';

// ==================== STYLED COMPONENTS ====================

const GridWrapper = styled.div`
  .e-grid .e-headercell {
    background: #f8fafc;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6b7280;
    padding: 12px 16px;
    border-color: #e5e7eb;
  }

  .e-grid .e-rowcell {
    border-color: #e5e7eb;
    padding: 12px 16px;
    font-size: 14px;
    color: #1f2937;
    vertical-align: middle;
    white-space: normal;
    height: auto;
  }

  .e-grid .e-row {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f9fafb;
    }
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }

  /* prevent baked-in scrollbars */
  .e-grid .e-gridcontent,
  .e-grid .e-content {
    overflow: visible !important;
  }

  /* if any horizontal overflow sneaks in, hide it */
  .e-grid {
    overflow-x: hidden !important;
  }

  .e-grid .e-table {
    width: 100% !important;
  }
`;

// Owner column styles (from ChangeLogDrawer)
const OwnerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OwnerAvatar = styled(Avatar)`
  background: #10b981;
  flex-shrink: 0;
`;

const OwnerName = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1f2937;
`;

// Process chips styles (from FinancialStatementsPage)
const ProcessChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
`;

const ProcessChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #eff6ff;
  height: 24px;
  color: #1e40af;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const NoProcessText = styled.span`
  font-family: Inter;
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
`;

// Tooltip styles
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #1f2937;
  text-align: center;
  margin-bottom: 8px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #eff6ff;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563eb;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #dbeafe;
  }
`;

// ==================== COMPONENT ====================

export type EX1ProcessOwnersByProcessGridProps = {
  title?: string;
  minHeight?: number;
};

export const EX1ProcessOwnersByProcessGrid: React.FC<
  EX1ProcessOwnersByProcessGridProps
> = ({
  title = 'Listing of Process Owners by Process',
  minHeight = 220,
}) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const annualReportId = selectedReport?.annualReportId;

  const [rows, setRows] = useState<DashboardMetric12ProcessOwnerListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRows([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric12(annualReportId);
        if (!isMounted) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRows([]);
        setErrorMessage(
          err?.message || err?.Message || 'Failed to load Metric 12.',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const dataSource = useMemo(() => {
    return rows.map(r => ({
      ...r,
      id: r.processOwnerId,
    }));
  }, [rows]);

  // Navigation handlers
  const handleProcessClick = (e: React.MouseEvent, processId: string) => {
    e.stopPropagation(); // Prevent row click
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  const handleRowClick = (args: any) => {
    // Only navigate to EX7 Tab 1 if user is admin
    if (isAdmin && args?.data?.processOwnerId) {
      navigate('/certification?tab=dashboard', {
        state: { selectedProcessOwnerId: args.data.processOwnerId },
      });
    }
  };

  // Owner column template
  const ownerTemplate = (props: DashboardMetric12ProcessOwnerListing) => {
    return (
      <OwnerInfo>
        <OwnerAvatar size={32} icon={<UserOutlined />} />
        <OwnerName>{props.processOwnerName}</OwnerName>
      </OwnerInfo>
    );
  };

  // Assigned Processes column template
  const processesTemplate = (props: DashboardMetric12ProcessOwnerListing) => {
    const processes = props.assignedProcesses || [];

    if (processes.length === 0) {
      return <NoProcessText>No processes assigned</NoProcessText>;
    }

    // Tooltip content showing all processes
    const allProcessesTooltip = (
      <div>
        <TooltipTitle>All Processes</TooltipTitle>
        <TooltipList>
          {processes.map(
            (process: DashboardMetric12AssignedProcess, index: number) => (
              <TooltipItem
                key={index}
                onClick={e => handleProcessClick(e, process.processId)}
              >
                {`${process.processCode} ${process.processName}`}
              </TooltipItem>
            ),
          )}
        </TooltipList>
      </div>
    );

    return (
      <ProcessChipsWrapper>
        {processes.map(
          (process: DashboardMetric12AssignedProcess, index: number) => (
            <Tooltip
              key={index}
              title={allProcessesTooltip}
              placement='top'
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '12px',
                borderRadius: '10px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color='#FFFFFF'
            >
              <ProcessChip
                onClick={e => handleProcessClick(e, process.processId)}
              >
                {`${process.processCode} ${process.processName}`}
              </ProcessChip>
            </Tooltip>
          ),
        )}
      </ProcessChipsWrapper>
    );
  };

  return (
    <Card title={title}>
      {loading ? (
        <div
          style={{
            minHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : errorMessage ? (
        <div style={{ padding: 16 }}>
          <Alert
            type='error'
            message='Dashboard Metric 12 Error'
            description={errorMessage}
            showIcon
          />
        </div>
      ) : (
        <GridWrapper>
          <GridComponent
            dataSource={dataSource}
            height='auto'
            width='100%'
            allowSorting={false}
            allowFiltering={false}
            enableHover={true}
            allowTextWrap={true}
            rowSelected={handleRowClick}
          >
            <ColumnsDirective>
              <ColumnDirective
                field='processOwnerName'
                headerText='Owner'
                width={320} // pixel width
                textAlign='Left'
                template={ownerTemplate}
                clipMode='EllipsisWithTooltip'
              />
              <ColumnDirective
                field='assignedProcesses'
                headerText='Assigned Processes'
                width={680} // pixel width to avoid overflow
                textAlign='Left'
                template={processesTemplate}
              />
            </ColumnsDirective>
          </GridComponent>
        </GridWrapper>
      )}
    </Card>
  );
};

export default EX1ProcessOwnersByProcessGrid;
